/**
 * <facet-filters> — intercepts filter form changes/submits and re-fetches the
 * collection / search section via Section Rendering API, then swaps the grid
 * (and the filter sidebar so counts update).
 *
 * Wraps a <form> that contains storefront filter inputs. Looks up:
 *   data-section-id   the section to refetch (e.g. "main-collection")
 *   data-grid         selector for the part of the section that holds product grid
 *   data-facets       selector for the filter form sidebar
 *
 * On change:
 *   1. Build URL: currentURL with form's serialized inputs.
 *   2. Fetch ${URL}?section_id=${section}.
 *   3. Replace section's #${data-grid} and #${data-facets} from response.
 *   4. history.pushState the filter URL.
 */

class FacetFilters extends globalThis.ThemeElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.gridSelector = this.dataset.gridSelector || '[data-product-grid]';
    this.facetsSelector = this.dataset.facetsSelector || '[data-facets]';
    this.form = this.$('form');

    const debouncedSubmit = globalThis.JunneTheme.debounce(this.submit.bind(this), 300);

    /* Live-update on filter form change. */
    if (this.form) {
      this.on(this.form, 'change', (e) => {
        e.preventDefault();
        debouncedSubmit();
      });
      this.on(this.form, 'submit', (e) => {
        e.preventDefault();
        this.submit();
      });
    }

    /* Sort dropdown is rendered outside the filter form (in the toolbar);
       wire it here so the listener is per-instance and cleans up on disconnect. */
    this.on(this, 'change', (e) => {
      const sel = e.target.closest('[data-sort-by]');
      if (!sel) return;
      this.syncSortHidden(sel.value);
      debouncedSubmit();
    });

    /* Intercept clicks on filter "remove" links and "Clear all". */
    this.on(this, 'click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#')) return;
      const url = new URL(href, window.location.origin);
      if (url.pathname !== window.location.pathname) return;
      e.preventDefault();
      this.fetchAndSwap(url.toString());
    });

    /* Browser back/forward. */
    this.on(window, 'popstate', () => this.fetchAndSwap(window.location.href, false));
  }

  syncSortHidden(value) {
    if (!this.form) return;
    let hidden = this.form.querySelector('input[name="sort_by"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'sort_by';
      this.form.prepend(hidden);
    }
    hidden.value = value;
  }

  submit() {
    const params = new URLSearchParams();
    if (this.form) {
      const formData = new FormData(this.form);
      for (const [k, v] of formData.entries()) {
        if (v !== '' && v != null) params.append(k, v);
      }
    }
    const sortSel = this.querySelector('[data-sort-by]');
    if (sortSel && sortSel.value) params.set('sort_by', sortSel.value);
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? '?' + qs : ''}`;
    this.fetchAndSwap(url);
  }

  async fetchAndSwap(url, push = true) {
    this.classList.add('is-loading');
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      const res = await fetch(u.toString());
      if (!res.ok) throw new Error(`facet fetch ${res.status}`);
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = parsed.querySelector(this.gridSelector);
      const oldGrid = document.querySelector(this.gridSelector);
      if (newGrid && oldGrid) oldGrid.replaceWith(newGrid);

      const newFacets = parsed.querySelector(this.facetsSelector);
      const oldFacets = document.querySelector(this.facetsSelector);
      if (newFacets && oldFacets) oldFacets.replaceWith(newFacets);

      const newCount = parsed.querySelector('[data-results-count]');
      const oldCount = document.querySelector('[data-results-count]');
      if (newCount && oldCount) oldCount.replaceWith(newCount);

      if (push) {
        const cleanUrl = url.split('?');
        const cleanQs = cleanUrl[1] ? cleanUrl[1].split('&').filter(p => !p.startsWith('section_id=')).join('&') : '';
        history.pushState({}, '', `${cleanUrl[0]}${cleanQs ? '?' + cleanQs : ''}`);
      }
    } catch (err) {
      console.error('facet-filters', err);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
